import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Container,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    CircularProgress,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Divider,
    Alert,
    AlertColor,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Snackbar
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TimerIcon from '@mui/icons-material/Timer';
import CircleIcon from '@mui/icons-material/Circle';
import HelpIcon from '@mui/icons-material/Help';
import LockIcon from '@mui/icons-material/Lock';
import RepeatIcon from '@mui/icons-material/Repeat';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import { SubscriptionService } from '../../services/modules/subscription';
import { getSubscriptionPlans } from '../../constants/subscription';
import type { SubscriptionPlan } from '../../services/types/subscription';
import { stripePromise } from '../../services/modules/stripe';


interface QuestionPaper {
    paper_id: number;
    title: string;
    description: string;
    theme_title: string;
    activation_date: string;
    expiry_date: string;
    questions_count: number;
    time_per_question: number;
    pass_mark: number;
    max_attempts: number;
    questions: any[];
    attempted?: boolean;
}

interface RawQuizAttempt {
    attempt_id: number;
    quiz_title: string;
    completed_at?: string;
    grade?: string;
    score?: number;
    passed?: boolean;
    failed_attempts: number;
    max_attempts: number;
}

interface QuizAttempt {
    attempt_id: number;
    quiz_title: string;
    is_completed: boolean;
    completed_at?: string;
    grade?: string;
    score?: number;
    passed: boolean;
    failed_attempts: number;
    max_attempts: number;
}

// Using imported SubscriptionPlan type from '../../services/types/subscription'

interface SubscriptionPlansDialogProps {
    open: boolean;
    onClose: () => void;
}

const SubscriptionPlansDialog: React.FC<SubscriptionPlansDialogProps> = ({ open, onClose }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [loadingPlanId, setLoadingPlanId] = useState<number | null>(null);

    const handleSubscribe = async (plan: SubscriptionPlan) => {
        try {
            if (!plan.available || loadingPlanId !== null) {
                return;
            }

            if (!user?.token) {
                window.location.href = '/login';
                return;
            }

            setLoadingPlanId(plan.id);

            // Get Stripe.js instance
            const stripe = await stripePromise;
            if (!stripe) {
                console.error('Failed to load Stripe');
                setLoadingPlanId(null);
                return;
            }

            const response = await SubscriptionService.createCheckoutSession(
                user.token,
                `${process.env.REACT_APP_FRONTEND_URL}/student/quizzes`,
                plan.id
            );

            // Redirect to Stripe Checkout using the URL
            window.location.href = response.url;
        } catch (err) {
            console.error('Failed to initiate subscription:', err);
            setLoadingPlanId(null);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle sx={{ textAlign: 'center', py: 3 }}>
                <Box>
                    <Typography variant="h4" component="div" fontWeight="bold">
                        {t('subscription.choosePlan')}
                    </Typography>
                    <Typography variant="subtitle1" component="div" color="text.secondary" sx={{ mt: 1 }}>
                        {t('subscription.choosePlanSubtitle')}
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={3} sx={{ py: 2 }}>
                    {getSubscriptionPlans(t).map((plan: SubscriptionPlan) => (
                        <Grid item xs={12} md={4} key={plan.id}>
                            <Card 
                                sx={{ 
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    opacity: plan.available ? 1 : 0.7
                                }}
                            >
                                {!plan.available && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%) rotate(-30deg)',
                                            backgroundColor: 'primary.main',
                                            color: 'white',
                                            py: 1,
                                            px: 4,
                                            zIndex: 1
                                        }}
                                    >
                                        <Typography variant="h6" sx={{ fontStyle: 'italic' }}>{t('subscription.comingSoon')}</Typography>
                                    </Box>
                                )}
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Typography variant="h5" gutterBottom fontWeight="bold" textAlign="center">
                                        {plan.name}
                                    </Typography>
                                    <Box sx={{ textAlign: 'center', my: 2, minHeight: '100px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        {plan.available ? (
                                            <>
                                                <Typography variant="h4" color="primary" gutterBottom>
                                                    {t('subscription.monthlyPrice', { price: plan.monthlyPrice })}
                                                    <Typography variant="subtitle1" component="span" color="text.secondary">
                                                        {t('subscription.perMonth')}
                                                    </Typography>
                                                </Typography>
                                                {plan.yearlyPrice && (
                                                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                                                        {t('subscription.yearlyBilling', { price: plan.yearlyPrice })}
                                                    </Typography>
                                                )}
                                            </>
                                        ) : (
                                            <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                {t('subscription.pricingAnnouncement')}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Divider sx={{ my: 2 }} />
                                    <Stack spacing={2}>
                                        {plan.features.map((feature: string, index: number) => (
                                            <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                                                <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                                                <Typography>{feature}</Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </CardContent>
                                <CardActions sx={{ p: 2, pt: 0 }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleSubscribe(plan)}
                                        disabled={!plan.available || loadingPlanId !== null}
                                        sx={{
                                            py: 1.5,
                                            '&.Mui-disabled': {
                                                backgroundColor: loadingPlanId === plan.id ? 'primary.main' : 'action.disabledBackground',
                                                color: loadingPlanId === plan.id ? 'white' : 'action.disabled'
                                            }
                                        }}
                                    >
                                        {loadingPlanId === plan.id ? (
                                            <CircularProgress size={24} color="inherit" />
                                        ) : (
                                            plan.available ? t('subscription.subscribe') : t('subscription.comingSoon')
                                        )}
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                <Button 
                    onClick={onClose} 
                    variant="outlined" 
                    color="primary"
                    disabled={loadingPlanId !== null}
                    sx={{ px: 4 }}
                >
                    {t('subscription.cancel')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const AvailableQuizzes: React.FC = () => {
    const { t } = useTranslation();
    const [quizzes, setQuizzes] = useState<QuestionPaper[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>('info');
    const { user, refreshUser } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [themes, setThemes] = useState<any[]>([]);
    const [papersByTheme, setPapersByTheme] = useState<Map<number, any[]>>(new Map());
    const [attempts, setAttempts] = useState<QuizAttempt[]>([]);
    const [selectedQuiz, setSelectedQuiz] = useState<QuestionPaper | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [subscriptionPlansOpen, setSubscriptionPlansOpen] = useState(false);
    const [maxFreeCertificates, setMaxFreeCertificates] = useState<number>(1); // Default to 1

    // Fetch max free certificates from system settings
    useEffect(() => {
        if (user?.token) {
            // Use the correct endpoint path without /api/ since it's in the base URL
            api.get('/admin/settings/max-free-certificates')
                .then(response => {
                    const maxCerts = response.data?.value || 5;
                    setMaxFreeCertificates(maxCerts);
                })
                .catch(error => {
                    console.error('Error fetching max free certificates:', error);
                    // Fallback to default value if endpoint fails
                    setMaxFreeCertificates(5);
                });
        }
    }, [user?.token]);

    const fetchAttempts = useCallback(async () => {
        if (!user?.token) return;
        
        try {
            const response = await api.get('/student/quiz-attempts');
            
            // Transform attempts data focusing on paper completion status
            const validAttempts = Array.isArray(response.data) ? response.data
                .map(attempt => {
                    return {
                        attempt_id: attempt.attempt_id,
                        quiz_title: attempt.quiz_title,
                        is_completed: true, // If we have the attempt, it's completed
                        completed_at: attempt.completed_at,
                        grade: attempt.grade,
                        score: attempt.score,
                        passed: attempt.passed || false,
                        failed_attempts: attempt.failed_attempts || 0,
                        max_attempts: attempt.max_attempts || 3 // Default to 3 if not specified
                    };
                })
                .filter(attempt => {
                    const isValid = attempt && 
                        typeof attempt.attempt_id === 'number' &&
                        typeof attempt.quiz_title === 'string';
                    return isValid;
                }) : [];

            setAttempts(validAttempts);
        } catch (error) {
            setSnackbarMessage(t('quiz.fetchAttemptsFailed'));
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    }, [user?.token]);

    useEffect(() => {
        fetchAttempts();
    }, [fetchAttempts]);

    const isQuizDisabled = useCallback((quiz: QuestionPaper) => {
        if (!quiz || !quiz.title) {
            return false;
        }

        // Check if quiz is in the future
        const activationDate = new Date(quiz.activation_date).getTime();
        const now = new Date().getTime();
        const isFuture = activationDate > now;

        // Find completed attempt for this paper by matching titles
        const completedAttempt = attempts.find(attempt => attempt.quiz_title === quiz.title);
        
        // Disable if:
        // 1. Quiz is in the future
        // 2. Quiz has been passed
        // 3. Failed attempts have reached max_attempts
        return isFuture || 
               (completedAttempt && completedAttempt.passed) ||
               (completedAttempt && completedAttempt.failed_attempts >= completedAttempt.max_attempts);
    }, [attempts]);

    interface ButtonProps {
        disabled: boolean;
        label: string;
        hasCompletedAttempt: boolean;
        onClick?: () => void;
    }

    const getButtonProps = useCallback((quiz: QuestionPaper): ButtonProps => {
        const isDisabled = isQuizDisabled(quiz);
        const completedAttempt = attempts.find(attempt => attempt.quiz_title === quiz.title);
        
        let label = t('quiz.startQuizButton');
        let disabled = isDisabled;

        if (completedAttempt) {
            if (completedAttempt.passed) {
                label = t('quiz.passedButton');
                disabled = true;
            } else if (completedAttempt.failed_attempts >= completedAttempt.max_attempts) {
                label = t('quiz.attemptsFinishedButton');
                disabled = true;
            } else {
                label = t('quiz.attemptAgainButton');
                disabled = false;
            }
        } else if (isDisabled) {
            label = t('quiz.notAvailableButton');
            disabled = true;
        }

        return {
            disabled: Boolean(disabled),
            label,
            hasCompletedAttempt: Boolean(completedAttempt),
            onClick: disabled ? undefined : () => {
                setSelectedQuiz(quiz);
                setOpenDialog(true);
            }
        };
    }, [isQuizDisabled, attempts]);

    useEffect(() => {
        const handleFocus = () => {
            if (user?.token) {
                fetchAttempts();
            }
        };

        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [user?.token, fetchAttempts]);

    const getQuizStatus = useCallback((quiz: QuestionPaper) => {
        const now = new Date().getTime();
        const activationDate = new Date(quiz.activation_date).getTime();
        const expiryDate = quiz.expiry_date ? new Date(quiz.expiry_date).getTime() : null;
        
        if (activationDate > now) {
            return {
                label: t('quiz.comingSoon'),
                color: '#ed6c02',
                bgColor: '#fff3e0'
            };
        } else if (expiryDate && expiryDate < now) {
            return {
                label: t('quiz.past'),
                color: '#1976d2',
                bgColor: '#e3f2fd'
            };
        } else {
            return {
                label: t('quiz.current'),
                color: '#2e7d32',
                bgColor: '#e8f5e9'
            };
        }
    }, []);

    const getPassMark = (questionPaper: any) => {
        return questionPaper.pass_mark || 50; // Default to 50% if not specified
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const sessionId = searchParams.get('session_id');

        if (sessionId && user?.token) {
            // Verify the session only once
            SubscriptionService.verifySession(sessionId, user.token)
                .then(async () => {
                    setSnackbarMessage(t('subscription.success'));
                    setSnackbarSeverity('success');
                    setSnackbarOpen(true);
                    
                    // Add a small delay to ensure webhook is processed
                    await new Promise(resolve => setTimeout(resolve, 2000));
                    await refreshUser();  // Refresh user data to update subscription status
                    navigate('/student/quizzes', { replace: true });
                })
                .catch((error) => {
                    console.error('Failed to verify session:', error);
                    setSnackbarMessage(t('subscription.verificationFailed'));
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                });
        }
    }, [user, location.search, navigate, refreshUser]); // Only run when user or location.search changes

    // Fetch themes only once on component mount
    useEffect(() => {
        if (user?.token) {
            const fetchData = async () => {
                try {
                    const themesData = await api.get('/quiz/themes');
                    setThemes(themesData.data);
                } catch (error) {
                    console.error('Failed to fetch themes:', error);
                    setSnackbarMessage(t('quiz.fetchThemesFailed'));
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                }
            };
            fetchData();
        }
    }, [user]);

    // Fetch papers only when themes change
    useEffect(() => {
        if (themes.length > 0 && user?.token) {
            const fetchPapers = async () => {
                try {
                    const paperPromises = themes.map(theme => 
                        api.get(`/quiz/themes/${theme.theme_id}/papers`)
                    );
                    const results = await Promise.all(paperPromises);
                    const newPapersByTheme = new Map();
                    themes.forEach((theme, index) => {
                        newPapersByTheme.set(theme.theme_id, results[index].data.map((paper: any) => ({
                            ...paper,
                            theme_title: theme.title
                        })));
                    });
                    setPapersByTheme(newPapersByTheme);
                    const allPapers = Array.from(newPapersByTheme.values()).flat();
                    const sortedPapers = allPapers.sort((a: any, b: any) => {
                        const dateA = new Date(a.activation_date).getTime();
                        const dateB = new Date(b.activation_date).getTime();
                        return dateB - dateA;
                    });
                    setQuizzes(sortedPapers);
                } catch (error) {
                    console.error('Failed to fetch papers:', error);
                    setSnackbarMessage(t('quiz.fetchPapersFailed'));
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                } finally {
                    setLoading(false);
                }
            };
            fetchPapers();
        }
    }, [themes, user]);

    const handleQuizClick = (quiz: QuestionPaper) => {
        // If quiz is locked, show subscription plans dialog
        if (isQuizDisabled(quiz)) {
            setSubscriptionPlansOpen(true);
            return;
        }

        // Otherwise, show start quiz dialog
        setSelectedQuiz(quiz);
        setOpenDialog(true);
    };

    const handleStartQuiz = useCallback(async () => {
        if (!selectedQuiz || !user?.token) {
            setError('Invalid quiz selection');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Check if quiz is available based on activation date
            const nowObj = new Date();
            const now = nowObj.getTime();
            const activationDateObj = new Date(selectedQuiz.activation_date);
            const activationDate = activationDateObj.getTime();
            
            // Debug logs
            console.log('Current time:', nowObj.toString());
            console.log('Current timestamp:', now);
            console.log('Activation date:', activationDateObj.toString());
            console.log('Activation timestamp:', activationDate);
            console.log('Is activation date in future?', activationDate > now);
            
            // Restore date check since it's working correctly
            if (activationDate > now) {
                setError('This quiz is not available yet');
                setLoading(false);
                return;
            }
            
            // Create quiz attempt and get questions
            const response = await api.post(`/student/quiz/${selectedQuiz.paper_id}/start`);
            const { attempt_id } = response.data;

            if (!attempt_id) {
                throw new Error('No attempt ID received from server');
            }

            // Navigate to the quiz page with the attempt ID
            navigate(`/quiz/${attempt_id}`);
            setOpenDialog(false);
        } catch (error: any) {
            console.error('Quiz start error:', error);
            console.log('Error response:', error.response?.data);
            setError(error.response?.data?.error || 'Failed to start quiz. Please try again.');
            setLoading(false);
        }
    }, [selectedQuiz, user?.token, navigate]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleSubscribe = async () => {
        try {
            if (!user?.token) {
                console.error('User not authenticated');
                return;
            }

            // Get Stripe.js instance
            const stripe = await stripePromise;
            if (!stripe) {
                console.error('Failed to load Stripe');
                return;
            }

            const response = await SubscriptionService.createCheckoutSession(user.token);

            // Redirect to Stripe Checkout using the URL
            window.location.href = response.url;
        } catch (err) {
            console.error('Checkout error:', err);
            setSnackbarMessage('Failed to start checkout process');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ mt: 4, mb: 4 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h4">
                                {t('quiz.availableQuizzes')}
                            </Typography>
                            {!user?.is_subscribed && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setSubscriptionPlansOpen(true)}
                                    sx={{
                                        mt: 2,
                                        minWidth: '300px',
                                        fontWeight: 600
                                    }}
                                >
                                    {t('quiz.subscribeButton')}
                                </Button>
                            )}
                        </Box>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {user?.is_subscribed ? (
                                <>
                                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                                        {t('quiz.subscriptionExpires', { 
                                            date: user.subscription_end_date ? 
                                                new Date(user.subscription_end_date).toLocaleDateString('en-GB', {
                                                    day: 'numeric',
                                                    month: 'short',
                                                    year: 'numeric'
                                                }) : 'N/A'
                                        })}
                                    </Typography>
                                    <Typography>
                                        {t('quiz.subscribedCertificateInfo')}
                                    </Typography>
                                </>
                            ) : (
                                <Box component="div" sx={{ mt: 1 }}>
                                    <ul style={{ paddingLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
                                        <li>{t('quiz.unsubscribedInfo.attemptsInfo')}</li>
                                        <li>{t('quiz.unsubscribedInfo.certificateInfo')}</li>
                                        <li>{t('quiz.unsubscribedInfo.freeCertificateInfo', { count: maxFreeCertificates })}</li>
                                        <li>{t('quiz.unsubscribedInfo.subscriptionInfo')}</li>
                                    </ul>
                                </Box>
                            )}
                        </Alert>
                    </Grid>

                    {error && (
                        <Grid item xs={12}>
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        </Grid>
                    )}

                    {quizzes.length === 0 ? (
                        <Grid item xs={12}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="body1" color="text.secondary">
                                    {t('quiz.noQuizzesAvailable')}
                                </Typography>
                            </Paper>
                        </Grid>
                    ) : (
                        quizzes.map((quiz) => {
                            const quizStatus = getQuizStatus(quiz);

                            return (
                                <Grid item xs={12} key={quiz.paper_id}>
                                    <Paper sx={{ p: 3, opacity: isQuizDisabled(quiz) ? 0.7 : 1 }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Box>
                                                <Typography variant="h6" gutterBottom>
                                                    {quiz.title}
                                                </Typography>
                                                <Typography variant="body1" color="text.secondary" gutterBottom>
                                                    {quiz.description}
                                                </Typography>
                                                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                                    <Chip
                                                        icon={<CircleIcon sx={{ fontSize: '14px !important' }} />}
                                                        label={quizStatus.label}
                                                        sx={{
                                                            color: quizStatus.color,
                                                            bgcolor: quizStatus.bgColor,
                                                            borderRadius: '4px',
                                                            minWidth: '100px',
                                                            '& .MuiChip-label': {
                                                                textAlign: 'center',
                                                                flex: 1
                                                            }
                                                        }}
                                                        size="small"
                                                    />
                                                    <Chip
                                                        icon={<TimerIcon sx={{ fontSize: '14px !important' }} />}
                                                        label={`${Math.floor(quiz.questions_count * quiz.time_per_question / 60)}m ${(quiz.questions_count * quiz.time_per_question) % 60}s`}
                                                        sx={{ borderRadius: '4px' }}
                                                        size="small"
                                                    />
                                                    {quiz.questions_count > 0 && (
                                                        <Chip
                                                            icon={<HelpIcon sx={{ fontSize: '14px !important' }} />}
                                                            label={t('quiz.questionsCount', { count: quiz.questions_count })}
                                                            sx={{ borderRadius: '4px' }}
                                                            size="small"
                                                        />
                                                    )}
                                                    <Chip
                                                        icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                                                        label={t('quiz.passMark', { mark: getPassMark(quiz) })}
                                                        sx={{ borderRadius: '4px' }}
                                                        size="small"
                                                    />
                                                    <Chip
                                                        icon={<RepeatIcon sx={{ fontSize: '14px !important' }} />}
                                                        label={t('quiz.attemptsCount', { count: quiz.max_attempts })}
                                                        sx={{ borderRadius: '4px' }}
                                                        size="small"
                                                    />
                                                </Box>
                                            </Box>
                                            <Box>
                                                <Button
                                                    variant="contained"
                                                    disabled={getButtonProps(quiz).disabled}
                                                    onClick={getButtonProps(quiz).onClick}
                                                    sx={{
                                                        minWidth: '200px',
                                                        backgroundColor: getButtonProps(quiz).hasCompletedAttempt ? 'success.main' : 'primary.main',
                                                        '&:hover': {
                                                            backgroundColor: getButtonProps(quiz).hasCompletedAttempt ? 'success.dark' : 'primary.dark',
                                                        }
                                                    }}
                                                >
                                                    {getButtonProps(quiz).label}
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            );
                        })
                    )}
                </Grid>
            </Box>

            {/* Start Quiz Dialog */}
            <Dialog 
                open={openDialog} 
                onClose={() => setOpenDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('quiz.startQuizDialog.title')}</DialogTitle>
                <DialogContent>
                    {selectedQuiz && (
                        <>
                            <Typography variant="h6" gutterBottom>
                                {selectedQuiz.title}
                            </Typography>
                            <Typography variant="body1" paragraph>
                                {selectedQuiz.description}
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemIcon>
                                        <TimerIcon />
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary={t('quiz.timeLimit', { minutes: Math.floor(selectedQuiz.questions_count * selectedQuiz.time_per_question / 60), seconds: (selectedQuiz.questions_count * selectedQuiz.time_per_question) % 60 })}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <HelpIcon />
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary={t('quiz.questionsCount', { count: selectedQuiz.questions_count })}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleIcon />
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary={t('quiz.passMark', { mark: getPassMark(selectedQuiz) })}
                                    />
                                </ListItem>
                            </List>
                            <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
                                {t('quiz.timerNote', 'Note: Once you start, the timer will begin and you cannot pause it.')}
                            </Typography>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                    <Button onClick={() => setOpenDialog(false)}>
                        {t('common.cancel')}
                    </Button>
                    <Button 
                        variant="contained" 
                        color="primary"
                        onClick={handleStartQuiz}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : t('quiz.startQuizButton')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Subscription Plans Dialog */}
            <SubscriptionPlansDialog
                open={subscriptionPlansOpen}
                onClose={() => setSubscriptionPlansOpen(false)}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
            >
                <Alert 
                    onClose={() => setSnackbarOpen(false)} 
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default AvailableQuizzes;
